import { useMemo, useState } from 'react';
import { AlertCircle, Calendar, CheckCircle2, Clock, ExternalLink, Eye, Heart, Image, Megaphone, MessageSquare, Play, Plus, Rocket, Sparkles, Trash2, TrendingUp, Trophy, Users, X } from 'lucide-react';
import { PostPreview, type PostPreviewData } from '@/components/PostPreview';
import { imgProxy } from '@/lib/img';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { CompetitiveGaps } from './CompetitiveGaps';
import { WhyItWorked } from './WhyItWorked';
import { BoostSignalsPanel } from './BoostSignalsPanel';
import {
  useIgCompetitorPostCounts,
  useIgCompetitorPosts,
  useIgCompetitorPostsRecent,
  useIgCompetitorSnapshots,
  useIgCompetitors,
  type IgCompetitor,
  type IgCompetitorSnapshot,
  type IgCompetitorPost,
} from '@/hooks/useCompetitors';
import { marketing } from '@/lib/supabase/marketing';
import { useQueryClient } from '@tanstack/react-query';
import { formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';

export function CompetitivePage() {
  const competitors = useIgCompetitors();
  const snapshots = useIgCompetitorSnapshots();
  const [selected, setSelected] = useState<string | null>(null);
  const [preview, setPreview] = useState<PostPreviewData | null>(null);
  const posts = useIgCompetitorPosts(selected ?? undefined, 50);
  const allPosts = useIgCompetitorPostsRecent(30, undefined, 500);
  const recentPosts = useIgCompetitorPostsRecent(10, selected ?? undefined, 200);

  const selectedFollowers = useMemo(() => {
    const row = snapshots.data?.find((s) => s.username === selected);
    return row?.followers_count ?? 0;
  }, [snapshots.data, selected]);

  const stats = useMemo(() => computeStats(posts.data ?? [], selectedFollowers), [
    posts.data,
    selectedFollowers,
  ]);
  const queryClient = useQueryClient();

  const remove = async (username: string, label: string) => {
    if (!confirm(`Remove @${username} (${label})?`)) return;
    const { error } = await marketing.from('ig_competitors').delete().eq('username', username);
    if (error) {
      alert('Could not delete: ' + error.message);
      return;
    }
    if (selected === username) setSelected(null);
    queryClient.invalidateQueries({ queryKey: ['ig-competitors'] });
    queryClient.invalidateQueries({ queryKey: ['ig-competitor-snapshots'] });
  };

  const fbAdLibraryUrl = (displayName: string | null, username: string): string => {
    const q = encodeURIComponent(displayName || username);
    return `https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=IN&q=${q}&search_type=keyword_unordered`;
  };

  const postCounts = useIgCompetitorPostCounts();

  const ranked = useMemo(
    () => buildLeaderboard(competitors.data ?? [], snapshots.data ?? [], postCounts.data ?? {}),
    [competitors.data, snapshots.data, postCounts.data],
  );

  const pending = ranked.filter((r) => r.syncStatus !== 'synced');

  return (
    <div className="space-y-8">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-500" />
            Competitive — Instagram
          </span>
        }
        description="Public profile + recent posts for tracked competitor handles."
      />

      <div className="flex flex-wrap items-center gap-3">
        <AddCompetitor />
        <span className="text-2xs text-muted-foreground">
          Icons per row:{' '}
          <ExternalLink className="inline h-3 w-3" /> open IG ·{' '}
          <Megaphone className="inline h-3 w-3" /> Meta Ad Library (currently-running FB/IG ads in India) ·{' '}
          <Trash2 className="inline h-3 w-3" /> remove
        </span>
      </div>

      {pending.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <div className="flex-1">
            <div className="font-medium">
              {pending.length} {pending.length === 1 ? 'handle' : 'handles'} awaiting first sync
            </div>
            <div className="mt-0.5 text-2xs text-muted-foreground">
              {pending.map((p) => '@' + p.username).join(', ')} — added but profile data hasn't been
              pulled from Instagram yet. Pings the dashboard team automatically on the next refresh
              cycle (typically within a few minutes).
            </div>
          </div>
        </div>
      )}

      <MotionCard>
        <Card>
          <CardHeader>
            <CardTitle>Leaderboard</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {competitors.isLoading ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : ranked.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title="No competitors tracked yet"
                  description="Add IG handles above to start tracking."
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Account</th>
                      <th className="px-3 py-2 text-right font-medium">Followers</th>
                      <th className="px-3 py-2 text-right font-medium">Following</th>
                      <th className="px-3 py-2 text-right font-medium">Posts</th>
                      <th className="px-3 py-2 text-right font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {ranked.map((row) => (
                      <tr
                        key={row.username}
                        className={cn(
                          'hover:bg-muted/30',
                          row.is_us && 'bg-violet-500/5',
                          selected === row.username && 'bg-accent/50',
                        )}
                      >
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => setSelected(row.username)}
                            className="text-left"
                          >
                            <div className="flex items-center gap-2 font-medium">
                              @{row.username}
                              {row.is_us && (
                                <Badge variant="muted" className="bg-violet-500/15 text-violet-600 dark:text-violet-400">
                                  Us
                                </Badge>
                              )}
                              {row.snapshot?.is_verified && (
                                <CheckCircle2 className="h-3 w-3 text-sky-500" />
                              )}
                            </div>
                            {row.display_name && (
                              <div className="text-2xs text-muted-foreground">{row.display_name}</div>
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {row.snapshot?.followers_count != null
                            ? formatNumberCompact(row.snapshot.followers_count)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-muted-foreground">
                          {row.snapshot?.follows_count != null
                            ? formatNumberCompact(row.snapshot.follows_count)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {row.snapshot?.media_count != null
                            ? formatNumberCompact(row.snapshot.media_count)
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <SyncStatusBadge
                            status={row.syncStatus}
                            postCount={row.postCount}
                            lastSyncedAt={row.snapshot?.snapshot_date}
                            addedAt={row.added_at}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-2">
                            <a
                              href={`https://instagram.com/${row.username}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-muted-foreground hover:text-foreground"
                              title="Open Instagram profile"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                            <a
                              href={fbAdLibraryUrl(row.display_name, row.username)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-muted-foreground hover:text-sky-500"
                              title="Search Meta Ad Library for currently running ads"
                            >
                              <Megaphone className="h-3.5 w-3.5" />
                            </a>
                            {!row.is_us && (
                              <button
                                onClick={() => remove(row.username, row.display_name || row.username)}
                                className="inline-flex items-center text-muted-foreground hover:text-rose-500"
                                title="Remove from tracking"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </MotionCard>

      {selected && (
        <MotionCard delay={0.05}>
          <Card>
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" /> @{selected} analytics
                <span className="text-2xs font-normal text-muted-foreground">last 30 days</span>
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>
                <X className="h-3 w-3" />
                Clear
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              <StatsGrid stats={stats} loading={posts.isLoading} />
              {stats.bestPost && (
                <BestPost
                  post={stats.bestPost}
                  onOpen={(p) =>
                    setPreview({
                      id: p.post_id,
                      caption: p.caption,
                      permalink: p.permalink,
                      imageUrl: p.image_url,
                      thumbnailUrl: p.thumbnail_url,
                      r2ThumbKey: p.r2_thumb_key,
                      r2MediaKey: p.r2_media_key,
                      mediaType: p.media_type,
                      postedAt: p.posted_at,
                      likes: p.like_count,
                      comments: p.comments_count,
                      views: p.views,
                      isBoosted: p.is_boosted,
                      source: `@${p.username}`,
                    })
                  }
                />
              )}
              <Image className="hidden" aria-hidden />
            </CardContent>
          </Card>
        </MotionCard>
      )}

      <MotionCard delay={0.06}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-500" /> Competitive gaps — strategic analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CompetitiveGaps
              competitors={competitors.data ?? []}
              snapshots={snapshots.data ?? []}
              posts={allPosts.data ?? []}
            />
          </CardContent>
        </Card>
      </MotionCard>

      <MotionCard delay={0.08}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" /> Why it worked — weekly teardown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <WhyItWorked
              competitors={competitors.data ?? []}
              snapshots={snapshots.data ?? []}
              posts={allPosts.data ?? []}
              onPreview={(p) => setPreview(p)}
            />
          </CardContent>
        </Card>
      </MotionCard>

      {/* Boost signals — inferred paid-vs-organic view from views/followers */}
      <MotionCard delay={0.09}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-rose-500" /> Boost signals
              <span className="text-2xs font-normal text-muted-foreground">inferred, last 30d</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BoostSignalsPanel onPreview={setPreview} />
          </CardContent>
        </Card>
      </MotionCard>

      <MotionCard delay={0.1}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Last 10 days
              <span className="text-2xs font-normal text-muted-foreground">
                {selected ? `@${selected}` : 'all competitors'} · newest first
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RecentPostsList
              posts={recentPosts.data ?? []}
              loading={recentPosts.isLoading}
              onPreview={setPreview}
            />
          </CardContent>
        </Card>
      </MotionCard>

      {selected && (
        <MotionCard delay={0.12}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-4 w-4" /> Top posts (by likes)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {posts.isLoading ? (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="aspect-square w-full" />
                  ))}
                </div>
              ) : (posts.data ?? []).length === 0 ? (
                <EmptyState
                  title="No posts yet"
                  description="Posts will populate after the first sync."
                />
              ) : (
                <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
                  {(posts.data ?? []).map((p) => {
                    const isVideo = p.media_type === 'VIDEO';
                    const looksLikeMp4 = (p.image_url ?? '').match(/\.mp4/i);
                    const videoSrc = isVideo && looksLikeMp4 ? p.image_url : null;
                    const gridSrc = isVideo
                      ? p.thumbnail_url
                      : p.image_url || p.thumbnail_url;
                    const caption = ((p.caption ?? '').split('\n')[0] ?? '').slice(0, 60);
                    return (
                      <button
                        type="button"
                        key={p.post_id}
                        onClick={() =>
                          setPreview({
                            id: p.post_id,
                            caption: p.caption,
                            permalink: p.permalink,
                            imageUrl: p.image_url,
                            thumbnailUrl: p.thumbnail_url,
                            r2ThumbKey: p.r2_thumb_key,
                            r2MediaKey: p.r2_media_key,
                            mediaType: p.media_type,
                            postedAt: p.posted_at,
                            likes: p.like_count,
                            comments: p.comments_count,
                            views: p.views,
                            isBoosted: p.is_boosted,
                            source: `@${p.username}`,
                          })
                        }
                        className="group relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted"
                      >
                        {isVideo && p.r2_media_key ? (
                          <video
                            src={imgProxy(null, p.r2_media_key)}
                            preload="metadata"
                            muted
                            playsInline
                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          />
                        ) : gridSrc ? (
                          <img
                            src={imgProxy(gridSrc, p.r2_thumb_key ?? (isVideo ? null : p.r2_media_key))}
                            alt=""
                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : videoSrc ? (
                          <video
                            src={imgProxy(videoSrc, p.r2_media_key)}
                            preload="metadata"
                            muted
                            playsInline
                            className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          />
                        ) : (
                          <div
                            className={cn(
                              'flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center',
                              isVideo
                                ? 'bg-gradient-to-br from-violet-500/15 to-rose-500/15'
                                : 'bg-muted',
                            )}
                          >
                            {isVideo ? (
                              <Image className="h-5 w-5 text-violet-500" />
                            ) : (
                              <Image className="h-5 w-5 text-muted-foreground" />
                            )}
                            {caption && (
                              <span className="line-clamp-2 text-2xs leading-tight text-muted-foreground">
                                {caption}
                              </span>
                            )}
                          </div>
                        )}
                        {isVideo && (
                          <span className="absolute left-1.5 top-1.5 rounded-md bg-black/60 p-1 backdrop-blur-sm">
                            <Image className="h-3 w-3 text-white" aria-hidden />
                            <span className="sr-only">Video</span>
                          </span>
                        )}
                        {p.is_boosted && (
                          <span
                            className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-md bg-sky-500/90 px-1.5 py-0.5 text-2xs font-medium text-white"
                            title="Currently boosted"
                          >
                            <Rocket className="h-2.5 w-2.5" />
                            Boosted
                          </span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-left text-2xs text-white">
                          ❤ {formatNumberCompact(p.like_count ?? 0)} · 💬 {formatNumberCompact(p.comments_count ?? 0)}
                          {p.views != null && p.views > 0 && (
                            <> · 👁 {formatNumberCompact(p.views)}</>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </MotionCard>
      )}

      <PostPreview post={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

type SyncStatus = 'pending' | 'partial' | 'synced';

interface LeaderboardRow extends IgCompetitor {
  snapshot?: IgCompetitorSnapshot;
  postCount: number;
  syncStatus: SyncStatus;
}

function buildLeaderboard(
  competitors: IgCompetitor[],
  snapshots: IgCompetitorSnapshot[],
  postCounts: Record<string, number>,
): LeaderboardRow[] {
  const latestByUser = new Map<string, IgCompetitorSnapshot>();
  for (const s of snapshots) {
    const existing = latestByUser.get(s.username);
    if (!existing || s.snapshot_date > existing.snapshot_date) {
      latestByUser.set(s.username, s);
    }
  }
  return competitors
    .map((c) => {
      const snapshot = latestByUser.get(c.username);
      const postCount = postCounts[c.username] ?? 0;
      const syncStatus: SyncStatus = !snapshot
        ? 'pending'
        : postCount === 0
          ? 'partial'
          : 'synced';
      return { ...c, snapshot, postCount, syncStatus };
    })
    .sort((a, b) => (b.snapshot?.followers_count ?? 0) - (a.snapshot?.followers_count ?? 0));
}

function SyncStatusBadge({
  status,
  postCount,
  lastSyncedAt,
  addedAt,
}: {
  status: SyncStatus;
  postCount: number;
  lastSyncedAt?: string;
  addedAt?: string;
}) {
  if (status === 'synced') {
    return (
      <div className="flex flex-col items-end gap-1 text-2xs">
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-3 w-3" />
          Synced
        </span>
        <span className="text-muted-foreground">
          {postCount} posts{lastSyncedAt ? ` · ${lastSyncedAt}` : ''}
        </span>
        <ProgressBar pct={100} tone="emerald" />
      </div>
    );
  }
  const pct = status === 'pending' ? 10 : 60;
  const tone = status === 'pending' ? 'amber' : 'sky';
  const label = status === 'pending' ? 'Awaiting sync' : 'Profile only';
  const Icon = status === 'pending' ? Clock : AlertCircle;
  const waitedHint = addedAt ? ` · added ${formatAgo(addedAt)}` : '';
  return (
    <div className="flex flex-col items-end gap-1 text-2xs">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium',
          tone === 'amber'
            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
            : 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
        )}
      >
        <Icon className="h-3 w-3" />
        {label} · {pct}%
      </span>
      <span className="text-muted-foreground">
        {status === 'pending' ? 'profile + posts pending' : 'posts pending'}
        {waitedHint}
      </span>
      <ProgressBar pct={pct} tone={tone} />
    </div>
  );
}

function ProgressBar({ pct, tone }: { pct: number; tone: 'amber' | 'sky' | 'emerald' }) {
  const fill =
    tone === 'amber' ? 'bg-amber-500' : tone === 'sky' ? 'bg-sky-500' : 'bg-emerald-500';
  return (
    <div className="h-1 w-24 overflow-hidden rounded-full bg-muted/50">
      <div className={cn('h-full transition-all', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

function formatAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function AddCompetitor() {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const submit = async () => {
    setError(null);
    const cleaned = username.trim().replace(/^@/, '').toLowerCase();
    if (!cleaned) {
      setError('Username required.');
      return;
    }
    setSubmitting(true);
    const { error: err } = await marketing
      .from('ig_competitors')
      .upsert({ username: cleaned, display_name: name.trim() || null, category: 'manual' });
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setUsername('');
    setName('');
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ['ig-competitors'] });
    queryClient.invalidateQueries({ queryKey: ['ig-competitor-snapshots'] });
    queryClient.invalidateQueries({ queryKey: ['ig-competitor-post-counts'] });
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="h-3.5 w-3.5" /> Add IG handle
      </Button>
    );
  }
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border/60 bg-card/50 p-3">
      <div>
        <label className="block text-2xs text-muted-foreground">IG handle</label>
        <input
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="@saladdays"
          className="h-9 w-48 rounded-md border border-border bg-background px-2.5 text-sm"
        />
      </div>
      <div>
        <label className="block text-2xs text-muted-foreground">Display name (optional)</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Salad Days"
          className="h-9 w-52 rounded-md border border-border bg-background px-2.5 text-sm"
        />
      </div>
      <Button size="sm" onClick={submit} disabled={submitting}>
        Save
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {error && <div className="text-2xs text-rose-500">{error}</div>}
      <div className="ml-auto flex items-center gap-1 text-2xs text-muted-foreground">
        <Users className="h-3 w-3" /> Data syncs from Supermetrics; ping the dashboard team to refresh.
      </div>
    </div>
  );
}

interface Stats {
  posts: number;
  postsPerWeek: number;
  spanDays: number;
  totalLikes: number;
  totalComments: number;
  totalViews: number;
  avgLikes: number;
  avgComments: number;
  avgViews: number;
  engagementRatePct: number;
  bestPost?: IgCompetitorPost;
  reelCount: number;
  reelsPerWeek: number;
  reelSharePct: number;
}

function computeStats(
  rows: IgCompetitorPost[],
  followers: number,
): Stats {
  if (rows.length === 0) {
    return {
      posts: 0,
      postsPerWeek: 0,
      spanDays: 0,
      totalLikes: 0,
      totalComments: 0,
      totalViews: 0,
      avgLikes: 0,
      avgComments: 0,
      avgViews: 0,
      engagementRatePct: 0,
      reelCount: 0,
      reelsPerWeek: 0,
      reelSharePct: 0,
    };
  }
  const dates = rows
    .map((r) => (r.posted_at ? new Date(r.posted_at).getTime() : 0))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const spanDays =
    dates.length > 1 ? Math.max(1, Math.round((dates[dates.length - 1]! - dates[0]!) / 86400000)) : 1;

  const totalLikes = rows.reduce((s, r) => s + (r.like_count ?? 0), 0);
  const totalComments = rows.reduce((s, r) => s + (r.comments_count ?? 0), 0);
  const totalViews = rows.reduce((s, r) => s + (r.views ?? 0), 0);
  const avgLikes = totalLikes / rows.length;
  const avgComments = totalComments / rows.length;
  const reels = rows.filter((r) => r.media_type === 'VIDEO');
  const avgViews = reels.length > 0
    ? reels.reduce((s, r) => s + (r.views ?? 0), 0) / reels.length
    : 0;

  const engagementRatePct = followers > 0
    ? ((avgLikes + avgComments) / followers) * 100
    : 0;

  const bestPost = [...rows].sort((a, b) => (b.like_count ?? 0) - (a.like_count ?? 0))[0];

  return {
    posts: rows.length,
    postsPerWeek: (rows.length * 7) / spanDays,
    spanDays,
    totalLikes,
    totalComments,
    totalViews,
    avgLikes,
    avgComments,
    avgViews,
    engagementRatePct,
    reelCount: reels.length,
    reelsPerWeek: (reels.length * 7) / spanDays,
    reelSharePct: (reels.length / rows.length) * 100,
    bestPost,
  };
}

function StatsGrid({ stats, loading }: { stats: Stats; loading: boolean }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
      <StatTile
        icon={<Calendar className="h-3.5 w-3.5" />}
        label="Posts / week"
        value={stats.postsPerWeek.toFixed(1)}
        sub={`${stats.posts} posts · ${stats.spanDays}d span`}
      />
      <StatTile
        icon={<Play className="h-3.5 w-3.5" />}
        label="Reels / week"
        value={stats.reelsPerWeek.toFixed(1)}
        sub={`${stats.reelCount} reels · ${stats.reelSharePct.toFixed(0)}% of posts`}
      />
      <StatTile
        icon={<Heart className="h-3.5 w-3.5" />}
        label="Avg likes / post"
        value={formatNumberCompact(Math.round(stats.avgLikes))}
        sub={`${formatNumberCompact(stats.totalLikes)} total`}
      />
      <StatTile
        icon={<Eye className="h-3.5 w-3.5" />}
        label="Avg reel views"
        value={stats.reelCount > 0 ? formatNumberCompact(Math.round(stats.avgViews)) : '—'}
        sub={`${formatNumberCompact(stats.totalViews)} total`}
      />
      <StatTile
        icon={<TrendingUp className="h-3.5 w-3.5" />}
        label="Engagement rate"
        value={`${stats.engagementRatePct.toFixed(2)}%`}
        sub="(likes + comments) / followers"
      />
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-md border border-border/40 bg-card/50 p-3">
      <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold tabular-nums text-xl">{value}</div>
      <div className="mt-0.5 text-2xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function BestPost({
  post,
  onOpen,
}: {
  post: IgCompetitorPost;
  onOpen: (post: IgCompetitorPost) => void;
}) {
  const isVideo = post.media_type === 'VIDEO';
  const src = post.image_url || post.thumbnail_url;
  const caption = ((post.caption ?? '').split('\n')[0] ?? '').slice(0, 140);
  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      className="group flex w-full items-start gap-4 rounded-md border border-border/40 bg-gradient-to-br from-amber-500/5 to-rose-500/5 p-3 text-left transition-colors hover:bg-amber-500/10"
    >
      <div className="relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border border-amber-500/40 bg-amber-500/10">
        {isVideo && post.r2_media_key ? (
          <>
            <video
              src={imgProxy(null, post.r2_media_key)}
              preload="metadata"
              muted
              playsInline
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
            />
            <Play
              className="absolute h-6 w-6 text-white drop-shadow-lg"
              fill="currentColor"
              aria-hidden
            />
          </>
        ) : src && !src.match(/\.mp4/i) ? (
          <img
            src={imgProxy(src, post.r2_thumb_key ?? post.r2_media_key)}
            alt=""
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <Trophy className="h-6 w-6 text-amber-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-2xs text-muted-foreground">
          <Trophy className="h-3 w-3 text-amber-500" />
          Best post by likes
          {post.posted_at && (
            <span>· {new Date(post.posted_at).toLocaleDateString('en-IN')}</span>
          )}
        </div>
        <div className="mt-1 text-sm leading-relaxed text-foreground/90">{caption}</div>
        <div className="mt-1.5 flex gap-3 text-2xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Heart className="h-3 w-3" /> {formatNumberCompact(post.like_count ?? 0)}
          </span>
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {formatNumberCompact(post.comments_count ?? 0)}
          </span>
          {post.views != null && (
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" /> {formatNumberCompact(post.views)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function RecentPostsList({
  posts,
  loading,
  onPreview,
}: {
  posts: IgCompetitorPost[];
  loading: boolean;
  onPreview: (p: PostPreviewData) => void;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="aspect-square w-full" />
        ))}
      </div>
    );
  }
  if (posts.length === 0) {
    return (
      <EmptyState
        title="No posts in the last 10 days"
        description="Either competitors have been quiet, or the sync needs a refresh. Trigger competitor-ig-sync to repopulate."
      />
    );
  }
  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
      {posts.map((p) => {
        const isVideo = p.media_type === 'VIDEO';
        const gridSrc = isVideo ? p.thumbnail_url : p.image_url || p.thumbnail_url;
        const daysAgo = p.posted_at
          ? Math.max(0, Math.floor((Date.now() - new Date(p.posted_at).getTime()) / 86400000))
          : null;
        return (
          <button
            type="button"
            key={p.post_id}
            onClick={() =>
              onPreview({
                id: p.post_id,
                caption: p.caption,
                permalink: p.permalink,
                imageUrl: p.image_url,
                thumbnailUrl: p.thumbnail_url,
                r2ThumbKey: p.r2_thumb_key,
                r2MediaKey: p.r2_media_key,
                mediaType: p.media_type,
                postedAt: p.posted_at,
                likes: p.like_count,
                comments: p.comments_count,
                views: p.views,
                isBoosted: p.is_boosted,
                source: `@${p.username}`,
              })
            }
            className="group relative aspect-square overflow-hidden rounded-md border border-border/40 bg-muted"
          >
            {isVideo && p.r2_media_key ? (
              <video
                src={imgProxy(null, p.r2_media_key)}
                preload="metadata"
                muted
                playsInline
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
              />
            ) : gridSrc ? (
              <img
                src={imgProxy(gridSrc, p.r2_thumb_key ?? (isVideo ? null : p.r2_media_key))}
                alt=""
                className="h-full w-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-500/15 to-rose-500/15">
                {isVideo ? <Play className="h-5 w-5 text-violet-500" /> : <Image className="h-5 w-5 text-muted-foreground" />}
              </div>
            )}
            <div className="absolute inset-x-0 top-0 flex items-center justify-between gap-1 bg-gradient-to-b from-black/60 to-transparent px-1.5 py-1 text-[10px] text-white">
              <span className="truncate font-medium">@{p.username}</span>
              {daysAgo !== null && (
                <span className="shrink-0 rounded-full bg-black/30 px-1.5 py-0.5 tabular-nums">
                  {daysAgo === 0 ? 'today' : `${daysAgo}d`}
                </span>
              )}
            </div>
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[10px] text-white">
              <Heart className="h-2.5 w-2.5" />
              <span className="tabular-nums">{formatNumberCompact(p.like_count ?? 0)}</span>
              {(p.views ?? 0) > 0 && (
                <>
                  <Eye className="ml-1 h-2.5 w-2.5" />
                  <span className="tabular-nums">{formatNumberCompact(p.views ?? 0)}</span>
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
